import { getRepository } from '@things-factory/shell'
import { Kpi } from './kpi'
import { KpiMetric } from '../kpi-metric/kpi-metric'
import { KpiValue, KpiValueInputType } from '../kpi-value/kpi-value'
import { KpiOrgScope } from '../kpi-org-scope/kpi-org-scope'
import { parseFormula, collectVariableNames } from '../../calculator/parser.js'
import { evaluateFormula } from '../../calculator/evaluator.js'
import { builtinFunctions } from '../../calculator/functions.js'
import { ObjectValueProvider } from '../../calculator/provider.js'
import { aggregateKpiMetricValue } from '../kpi-metric/aggregate-kpi-metric'
import { KpiValueScoreService } from '../kpi-value/kpi-value-score.service'

/**
 * KPI 단위 집계/산식 자동화 함수
 * @param kpiId KPI ID
 * @param domainId 도메인 ID
 * @param context ResolverContext
 * @returns 저장된 KPI Value 배열
 */
export async function aggregateKpiValue(kpiId: string, domainId: string, context: ResolverContext) {
  const tx = context.state?.tx || getRepository(Kpi).manager
  const scoreService = new KpiValueScoreService()

  // 1. KPI 정보 조회
  const kpi = await getRepository(Kpi).findOne({ where: { id: kpiId, domain: { id: domainId } } })
  if (!kpi) throw new Error('KPI 정보 없음')
  if (!kpi.active) throw new Error('비활성화된 KPI')
  if (!kpi.formula) throw new Error('KPI formula 없음')

  // 2. formula 를 AST 로 파싱하고, 참조 변수명(=metric 이름)을 AST 에서 추출한다.
  //    정규식 추출(extractMetricCodes)은 `[한글 변수]`·공백 포함 이름을 놓치므로 AST 기반이 정확.
  let ast
  try {
    ast = parseFormula(kpi.formula)
  } catch (e) {
    throw new Error(`KPI formula 파싱 실패 (kpi=${kpi.name}): ${(e as Error).message}`)
  }
  const metricCodes = collectVariableNames(ast)
  const codeValueMap: Record<string, any[]> = {}
  for (const code of metricCodes) {
    // 변수명으로 metric 찾기
    const metric = await getRepository(KpiMetric).findOne({ where: { name: code, domain: { id: domainId } } })
    if (!metric) throw new Error(`KPI formula metric '${code}' not found`)
    codeValueMap[code] = await aggregateKpiMetricValue(metric.id, domainId, context)
  }
  // org/date/period별로 값 매핑(가장 최근 기준, org key 조합)
  const orgKey = v => [v.date, v.period, v.org].join('|')
  const orgMap: Record<string, any> = {}
  for (const code of metricCodes) {
    for (const v of codeValueMap[code]) {
      const key = orgKey(v)
      orgMap[key] = orgMap[key] || { ...v, _values: {} }
      orgMap[key]._values[code] = v.value
    }
  }
  // formula 계산 — 안전한 AST 평가기 사용 (JS `Function()` eval 제거).
  //   · 변수(metric)는 ObjectValueProvider 로 org-combo 값 맵에서 해석
  //   · 평가 실패는 조용히 null 로 두지 않고 로그로 남긴다 (원인 추적)
  const savedValues = []
  for (const key in orgMap) {
    const ctx = orgMap[key]._values
    let value = null
    try {
      value = await evaluateFormula(ast, {
        functions: builtinFunctions,
        provider: new ObjectValueProvider(ctx)
      })
    } catch (e) {
      console.error(`[aggregateKpiValue] formula 평가 실패 (kpi=${kpi.name}, formula=${kpi.formula})`, e)
      value = null
    }
    const valueDate = orgMap[key].date
    const org = orgMap[key].org
    const version = kpi.version || 1
    if (value == null || isNaN(value)) continue
    // KpiOrgScope 처리 (org 문자열로 찾기 또는 생성)
    const kpiOrgScopeRepo = getRepository(KpiOrgScope, context.state?.tx)
    let kpiOrgScope = await kpiOrgScopeRepo.findOne({
      where: { org: org, domain: { id: domainId } }
    })
    if (!kpiOrgScope) {
      // 새 KpiOrgScope 생성
      kpiOrgScope = await kpiOrgScopeRepo.save({
        entityType: 'AutoAggregate',
        entityId: `auto-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        entityName: org,
        org: org,
        domain: kpi.domain
      })
    }

    // upsert(동일 KPI, valueDate, kpiOrgScope, version) 기준으로 저장
    const repo = getRepository(KpiValue, context.state?.tx)
    const existing = await repo.findOne({
      where: { kpi: { id: kpi.id }, valueDate, kpiOrgScope: { id: kpiOrgScope.id }, version, domain: { id: domainId } }
    })
    let entity = existing || repo.create()
    entity.kpi = kpi
    entity.kpiId = kpi.id
    entity.version = version
    entity.valueDate = valueDate
    entity.value = value
    entity.kpiOrgScope = kpiOrgScope
    entity.kpiOrgScopeId = kpiOrgScope.id
    entity.inputType = KpiValueInputType.AUTO
    entity.source = 'AUTO'
    entity.domain = kpi.domain
    entity.creator = context.state?.user
    entity.updater = context.state?.user

    // 성과 점수 자동 계산 및 저장
    await scoreService.calculateAndSaveScore(entity, kpi)

    entity = await repo.save(entity)
    savedValues.push(entity)
  }
  return savedValues
}
